/*jshint esversion: 6 */
/*! webmd.pim */

import { set, get, remove } from 'webmd-ads-cookie';

export let runIfAllowed;
export let isAllowed;
export let increment;
export let getCount;
export let checkExpiration;
export let metricsModule;

if (typeof window !== 'undefined') {
  /**
   * @namespace PIM - program interrupt management
   * This code will run on both the WebMD core and the O&O sites.
   */

  /**
   * Interruptions to allow per session
   * @type {Number}
   */
  const maxSession = 2;

  /**
   * Ad server cookie that records impression count
   * @type {Object}
   */
  let cookieCount = {
    type:'sess',
    prefix:'pim',
    value:'c',
    count:0
  };

  /**
   * Ad server cookie that records max impressions reached
   * @type {Object}
   */
  let cookieMax = {
    type:'sess',
    prefix:'pim',
    value:'m'
  };

  /**
   * Ad server cookie that records a timestamp: "pimt_[timestamp]x"
   * @type {Object}
   */
  let cookieExpire = {
    type:'sess',
    prefix:'pim',
    value:'t'
  };

  /**
   * Number of milliseconds for the cookie expiration
   * 86400000 == 1000ms * 60sec/min * 60min/hr * 24hr
   * @type {Number}
   */
  const expireMS = 86400000;

  /**
   * Module name to use for metrics call.
   * Set to blank to turn off metrics.
   */
  metricsModule = 'pim';

  /**
   * Has an intrusion occurred on this page already?
   * @private
   */
  let _occurredOnPage = false;

  /**
   * If the interrupt is allowed, increment the count, then run a callback function.
   *
   * @example
   * webmd.pim.runIfAllowed(function(){ alert('Interrupt!'); });
   * webmd.pim.runIfAllowed(function(){ alert('Interrupt!'); }, function(){ alert('No Interrupt!'); });
   *
   * @param  {Function} callback     Runs if interrupt is allowed.
   * @param  {Function} [nocallback] Runs if interrupt is not allowed.
   */
  runIfAllowed = function(callback, nocallback) {

    if (isAllowed()) {

      increment();

      if (callback) {
        callback();
      }

    } else if (nocallback) {
      nocallback();
    }
  };

  /**
   * Determine if an interrupt is allowed.
   *
   * To be allowed, the following two tests must pass:
   *
   * An interruption must not have occurred already on this page.
   * AND
   * Less than the maxSession interrupts have occured during this session
   *
   * @return {Boolean} [description]
   */
  isAllowed = function(){

    // Before checking, expire the cookie if necessary
    checkExpiration();

    // Check if something on the page has already claimed the interrupt
    if ( _occurredOnPage ) {
      return false;
    }

    // Otherwise determine if we have exceeded the max number of interrupts
    return Boolean( getCount() < maxSession );
  };

  /**
   * Increment the interrupt counter.
   */
  increment = function(){

    let count = 0;

    // Before incrementing, expire the cookie if necessary
    //this.checkExpiration();

    // Immediately mark this interrupt for the page
    _occurredOnPage = true;

    // Get the current count from the cookie and increment it
    count = getCount() + 1;

    // Save the new count in the cookie
    cookieCount.count = count;
    set(cookieCount);

    // If the count has reached the maximum per session, also set pim_max in the cookie
    if (count >= maxSession) {
      set(cookieMax);
    }

    // Also set the current timestamp in the cookie
    cookieExpire.count = new Date().getTime();
    set(cookieExpire);

    return true;
  };

  /**
   * Returns the current number of interrupts from the count cookie
   */
  getCount = function() {

    let o, count;

    // Before checking, expire the cookie if necessary
    //this.checkExpiration();

    // Try to get the cookie

    o = get(cookieCount.type);

    if (o[cookieCount.prefix] && o[cookieCount.prefix][cookieCount.value]) {

      // Extract the count from the cookie value like "pimc_2x"
      count = o[cookieCount.prefix][cookieCount.value].replace(/.*_(\d+)x$/, '$1');

      // Convert the count to a number, or return zero if something went wrong
      return parseInt(count, 10) || 0;
    }

    // If the cookie is not set (or was not set correctly for some reason) assume zero count

    return 0;
  };

  /**
   * Delete all PIM cookie values if the timestamp is expired
   */
  checkExpiration = function() {

    let o, timestamp, now = new Date().getTime();

    // Get the ads cookie object that contains the PIM values
    o = get(cookieExpire.type);

    // Make sure the PIM data is part of the cookie and that it has a timestamp value
    if (o[cookieExpire.prefix] && o[cookieExpire.prefix][cookieExpire.value]) {

      // Extract the timestamp from the cookie value like "pimt_1321566886068x"
      timestamp = o[cookieExpire.prefix][cookieExpire.value].replace(/.*_(\d+)x$/, '$1');

      // Convert the count to a number, or return zero if something went wrong
      timestamp = parseInt(timestamp, 10) || 0;
    }

    if (!timestamp) {
      return;
    }

    // Check if the cookie should be expired
    if (timestamp + expireMS < now) {
      // Remove all cookies with the pim prefix
      remove({type: cookieExpire.type, prefix: cookieExpire.prefix});
    }
  };

  // exposing the PIM object to the window
  window.webmd = window.webmd || {};
  window.webmd.pim = {
    runIfAllowed,
    isAllowed,
    increment,
    getCount,
    checkExpiration,
    metricsModule
  };
}